import React, { useEffect, useState } from "react";
import { Form, Input, Select, Switch, message } from "antd";
import FormModal from "../../../components/FormModal";
import PasswordFields from "../../../components/PasswordFields";
import { extractMsg } from "../../../utils/errorUtils";

const ROLE_OPTIONS = [
  { label: "Admin", value: 1 },
  { label: "Public", value: 2 },
  { label: "Alpha", value: 3 },
  { label: "Gamma", value: 4 },
  { label: "SQL Lab", value: 5 },
];

export function AddUserDialog({ open, onClose, onSave }) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) form.resetFields();
  }, [open, form]);

  const handleFinish = async (values) => {
    setLoading(true);
    try {
      await onSave(values);
      form.resetFields();
      onClose();
    } catch (error) {
      message.error(extractMsg(error, "Failed to create user"));
      console.error("Failed to create user", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <FormModal
      title="Add Skeyechart User"
      open={open}
      onClose={() => {
        form.resetFields();
        onClose();
      }}
      onFinish={handleFinish}
      loading={loading}
      form={form}
      submitText="Add User"
      initialValues={{ active: true }}
    >
      <Form.Item
        name="firstName"
        label="First Name"
        rules={[{ required: true }]}
      >
        <Input />
      </Form.Item>
      <Form.Item name="lastName" label="Last Name" rules={[{ required: true }]}>
        <Input />
      </Form.Item>
      <Form.Item
        name="username"
        label="Username"
        rules={[{ required: true, min: 6, max: 24 }]}
      >
        <Input />
      </Form.Item>
      <Form.Item
        name="email"
        label="Email"
        rules={[{ required: true, type: "email" }]}
      >
        <Input type="email" />
      </Form.Item>
      <PasswordFields />
      <Form.Item name="roleIds" label="Roles" rules={[{ required: true }]}>
        <Select
          mode="multiple"
          options={ROLE_OPTIONS}
          placeholder="Select Roles"
        />
      </Form.Item>
      <Form.Item name="active" label="Status" valuePropName="checked">
        <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
      </Form.Item>
    </FormModal>
  );
}

export function EditUserDialog({ open, user, onClose, onSave }) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && user) {
      const mappedRoleIds = (user.roles || [])
        .map((roleName) => {
          const match = ROLE_OPTIONS.find(
            (r) => r.label.toLowerCase() === roleName.toLowerCase(),
          );
          return match ? match.value : null;
        })
        .filter(Boolean);

      form.setFieldsValue({
        firstName: user.first_name,
        lastName: user.last_name,
        username: user.username,
        email: user.email,
        roleIds: mappedRoleIds,
        active: user.active,
      });
    }
  }, [open, user, form]);

  const handleFinish = async (values) => {
    setLoading(true);
    try {
      await onSave(values);
      onClose();
    } catch (error) {
      message.error(extractMsg(error, "Failed to edit user"));
      console.error("Failed to edit user", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <FormModal
      title="Edit Skeyechart User"
      open={open}
      onClose={onClose}
      onFinish={handleFinish}
      loading={loading}
      form={form}
      submitText="Update User"
    >
      <Form.Item
        name="firstName"
        label="First Name"
        rules={[{ required: true }]}
      >
        <Input />
      </Form.Item>
      <Form.Item name="lastName" label="Last Name" rules={[{ required: true }]}>
        <Input />
      </Form.Item>
      <Form.Item
        name="username"
        label="Username"
        rules={[{ required: true, min: 6, max: 24 }]}
      >
        <Input />
      </Form.Item>
      <Form.Item
        name="email"
        label="Email"
        rules={[{ required: true, type: "email" }]}
      >
        <Input type="email" />
      </Form.Item>
      <Form.Item
        name="roleIds"
        label="Roles"
        rules={[{ required: true, message: "Please select at least one role" }]}
      >
        <Select
          mode="multiple"
          options={ROLE_OPTIONS}
          placeholder="Select Roles"
        />
      </Form.Item>
      <Form.Item name="active" label="Status" valuePropName="checked">
        <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
      </Form.Item>
    </FormModal>
  );
}
